import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { useTheme } from 'context/ThemeContext';
import { useToast } from 'context/ToastContext';
import { copyToClipboard } from 'utils/helpers/copyToClipboard';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import type { ApiAccessCode, ApiKeyType, ApiOutEventCode } from '../types';
import type { ApiKeyFormState } from './uiTypes';
import { SETTINGS_DIR_NAME_MAX_LENGTH } from '../types';
import SolarIcon from 'components/icons/SolarIcon';

const ACCESS_IDS: ApiAccessCode[] = [
    'lead_create',
    'client_create',
    'product_create',
    'warehouse_move',
    'clients_list',
    'leads_list',
    'lead_events_list',
    'objects_list',
    'object_view',
    'warehouses_list',
    'warehouse_stocks',
    'warehouse_operations',
];
const EVENT_IDS: ApiOutEventCode[] = [
    'client_create',
    'lead_create',
    'lead_event_create',
    'product_create',
    'warehouse_income',
    'object_create_or_status_change',
    'object_create',
    'object_status_change',
    'object_events',
    'user_update',
    'user_delete',
    'object_delete',
];

interface ApiKeysFormProps {
    type: ApiKeyType;
    form: ApiKeyFormState;
    editId: number | null;
    showForm: boolean;
    isSaving: boolean;
    setForm: (form: ApiKeyFormState) => void;
    onGenerateKey: () => void;
    onReset: () => void;
    onSave: () => void;
}

const ApiKeysForm = ({ type, form, editId, showForm, isSaving, setForm, onGenerateKey, onReset, onSave }: ApiKeysFormProps) => {
    const { t } = useTranslation(['Settings', 'common']);
    const { darkTheme } = useTheme();
    const { showToast } = useToast();

    if (!showForm) return null;

    const options =
        type === 'in'
            ? ACCESS_IDS.map((value) => ({ value, label: t(`apiKeys.access.${value}`) }))
            : EVENT_IDS.map((value) => ({ value, label: t(`apiKeys.events.${value}`) }));
    const selected = type === 'in' ? form.accessJson : form.eventTypesJson;

    return (
        <Card className="mb-3">
            <Card.Body>
                <Row className="g-2">
                    <Col md={4}>
                        <Form.Label>{t('apiKeys.form.name')}</Form.Label>
                        <Form.Control
                            value={form.keyName}
                            maxLength={SETTINGS_DIR_NAME_MAX_LENGTH}
                            onChange={(e) => setForm({ ...form, keyName: e.target.value })}
                            placeholder={t(type === 'in' ? 'apiKeys.form.namePlaceholderIn' : 'apiKeys.form.namePlaceholderOut')}
                        />
                    </Col>
                    {type === 'out' && (
                        <Col md={4}>
                            <Form.Label>{t('apiKeys.form.webhookUrl')}</Form.Label>
                            <Form.Control
                                value={form.webhookUrl}
                                onChange={(e) => setForm({ ...form, webhookUrl: e.target.value })}
                                placeholder={t('apiKeys.form.webhookPlaceholder')}
                            />
                        </Col>
                    )}
                    {type === 'in' && (
                        <Col md={4}>
                            <Form.Label>{t('apiKeys.form.allowedDomains')}</Form.Label>
                            <Form.Control
                                value={form.allowedDomains}
                                onChange={(e) => setForm({ ...form, allowedDomains: e.target.value })}
                                placeholder={t('apiKeys.form.allowedDomainsPlaceholder')}
                            />
                        </Col>
                    )}
                    <Col md={4}>
                        <Form.Label>{t('apiKeys.form.apiKey')}</Form.Label>
                        <div className="d-flex flex-column gap-2">
                                <Form.Control
                                    value={form.apiKey}
                                    onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                                    placeholder={t('apiKeys.form.apiKey')}
                                />
                            <div className="d-flex gap-2 flex-wrap">
                                <Button size="sm" variant="outline-info" className="px-2 py-1" onClick={onGenerateKey}>
                                    <SolarIcon name="stars" className="me-1" />
                                    {t('apiKeys.form.btnGenerate')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    className="px-2 py-1"
                                    onClick={async () => {
                                        if (!form.apiKey) return;
                                        const copied = await copyToClipboard(form.apiKey);
                                        showToast(
                                            copied ? t('apiKeys.toasts.copied') : t('apiKeys.errors.copyFailed'),
                                            copied ? 'success' : 'danger',
                                        );
                                    }}
                                >
                                    <SolarIcon name="copy" className="me-1" />
                                    {t('apiKeys.form.btnCopy')}
                                </Button>
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row className="g-2 mt-1">
                    <Col md={12}>
                        <Form.Label>{t(type === 'in' ? 'apiKeys.form.accesses' : 'apiKeys.form.events')}</Form.Label>
                        <Select
                            options={options}
                            styles={{
                                ...(darkTheme ? selectStylesDark : selectStylesLigth),
                                control: (base, state) => ({
                                    ...base,
                                    ...((darkTheme ? selectStylesDark : selectStylesLigth).control?.(base, state) || {}),
                                }),
                            }}
                            value={options.filter((v) => selected.includes(v.value as ApiAccessCode & ApiOutEventCode))}
                            onChange={(value) =>
                                setForm({
                                    ...form,
                                    ...(type === 'in'
                                        ? { accessJson: value.map((v) => v.value as ApiAccessCode) }
                                        : { eventTypesJson: value.map((v) => v.value as ApiOutEventCode) }),
                                })
                            }
                            isMulti={true}
                            placeholder={t(type === 'in' ? 'apiKeys.form.accessPlaceholder' : 'apiKeys.form.eventsPlaceholder')}
                        />
                        <div className="mt-2">
                            <Form.Check
                                id={`active-${type}`}
                                type="switch"
                                label={t('apiKeys.form.isActive')}
                                checked={!!form.isActive}
                                onChange={(e) => setForm({ ...form, isActive: e.target.checked ? 1 : 0 })}
                            />
                        </div>
                    </Col>
                </Row>

                <div className="d-flex justify-content-end mt-3 gap-2">
                    <Button variant="light" onClick={onReset}>
                        {t('common:buttons.cancel')}
                    </Button>
                    <Button variant="success" onClick={onSave} disabled={isSaving}>
                        <SolarIcon name={editId ? 'check2' : 'plus'} className="me-1" />
                        {isSaving
                            ? editId
                                ? t('common:buttons.saving')
                                : t('common:buttons.creating')
                            : editId
                              ? t('common:buttons.save')
                              : t('common:buttons.create')}
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

export default ApiKeysForm;
